import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Flame, ChevronDown } from "lucide-react";
import { ORDER_URL } from "@/components/Navbar";
import { scrollToSection } from "@/App";

const HERO_IMG =
  "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?q=80&w=1920&auto=format&fit=crop";

const lines = [
  { text: "FIRE.", cls: "text-cream" },
  { text: "SPICE &", cls: "text-outline-gold" },
  { text: "SOUL.", cls: "text-gold italic" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen min-h-[680px] overflow-hidden bg-navy grain"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt="Charcoal-grilled kebab skewers over open flame"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#0A1128]/55" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/40 to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-10 flex flex-col justify-end pb-28 sm:pb-32"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.9 }}
          className="font-script text-gold text-2xl sm:text-3xl mb-4"
          data-testid="hero-script-accent"
        >
          authentic Pakistani &amp; Indian — Calamvale
        </motion.p>

        <h1 className="font-heading font-semibold leading-[0.92] tracking-tight text-[17vw] sm:text-[11vw] lg:text-[8.5rem]">
          {lines.map((line, i) => (
            <span key={line.text} className="mask-line">
              <motion.span
                className={line.cls}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{
                  delay: 0.35 + i * 0.16,
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-6 max-w-xl text-cream/75 text-base sm:text-lg font-light leading-relaxed"
          data-testid="hero-subcopy"
        >
          Slow-cooked curries, charcoal-fired kebabs and naan pulled hot from
          the tandoor — recipes carried across generations, served in the heart
          of Calamvale.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <motion.a
            data-testid="hero-order-online-btn"
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -3, boxShadow: "0 16px 44px rgba(198,40,40,0.5)" }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2.5 bg-spice text-cream font-semibold text-base sm:text-lg px-9 py-4 rounded-full"
          >
            <Flame size={19} />
            Order Online
          </motion.a>
          <button
            data-testid="hero-our-story-btn"
            onClick={() => scrollToSection("#about")}
            className="text-cream/85 hover:text-gold border border-cream/30 hover:border-gold/60 transition-colors duration-300 px-8 py-4 rounded-full text-base"
          >
            Our Story
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 right-8 sm:right-12 z-10 text-gold hidden sm:flex flex-col items-center gap-2"
        aria-label="Scroll down"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-body">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
